import find from 'lodash/find';
import orderBy from 'lodash/orderBy';
import filter from 'lodash/filter';
import map from 'lodash/map';
import type { NextRouter } from 'next/router';

import type { AppData } from '../../types/app_data';
import { AppStepSection, StepCategory } from '../../types/app_data';
import type { Step } from '../types/step';
import { RelationToUser } from '../../types/driver';
import { isEligibleForGeicoQuotes, isEligibleForMercuryQuotes } from '../../utils/onboard';
import type { SearchPolicyDriver } from '../../types/policy';
import { isValidOccupation, NON_EMPLOYMENT_OPTIONS } from '../../utils/occupations';
import { isRated } from '../../utils/driver';

const isNonEmployStatus = (driver: SearchPolicyDriver): boolean => {
  if (!driver?.occupation || !driver?.industry) {
    return true;
  }

  return !!NON_EMPLOYMENT_OPTIONS.find(
    (option) => option.occupation === driver.occupation && option.industry === driver.industry
  );
};

const canAskOtherDriverOccupation = (appData: AppData, driver: SearchPolicyDriver) => {
  if (appData.stepSection === AppStepSection.Challenge) {
    if (driver?.industry && driver.occupation && isValidOccupation(driver.occupation)) {
      return false;
    }
  }

  if (!driver || isNonEmployStatus(driver)) {
    return false;
  }

  return isEligibleForMercuryQuotes(appData);
};

const canAskSpouseOccupation = (appData: AppData) => {
  const state = appData?.address?.state || '';
  if (appData.signupParams?.needNNOInsurance === true) {
    return false;
  }

  const spouse = find(
    appData.drivers,
    (driver) => !driver.isArchived && driver.relationToUser === RelationToUser.Spouse
  );

  if (!spouse) {
    return false;
  }

  if (appData.stepSection === AppStepSection.Challenge) {
    // Skip the step is it's already answered
    if (spouse?.industry && spouse.occupation && isValidOccupation(spouse.occupation)) {
      return false;
    }
  }

  /**
   * Show this step if user select 'Employed'
   */
  if (!isNonEmployStatus(spouse)) {
    return isEligibleForGeicoQuotes(appData) || state === 'NC';
  }

  return false;
};

const getDriverListForOccupationList = (appData: AppData): Array<{ driver: SearchPolicyDriver; eligible: boolean }> => {
  const list = orderBy(
    map(appData.drivers, (driver) => {
      if (driver.isArchived || driver.relationToUser === RelationToUser.Insured) {
        return { driver, eligible: false };
      }

      if (!isRated(driver) && driver.relationToUser !== RelationToUser.Spouse) {
        return { driver, eligible: false };
      }

      if (isNonEmployStatus(driver)) {
        return { driver, eligible: false };
      }

      if (canAskOtherDriverOccupation(appData, driver)) {
        return { driver, eligible: true };
      }

      if (driver.relationToUser === RelationToUser.Spouse) {
        return { driver, eligible: canAskSpouseOccupation(appData) };
      }

      return { driver, eligible: false };
    }),
    [(item) => item?.driver?.id],
    ['asc']
  );

  return list;
};

export const getCurrentOccupationListDriver = (appData: AppData) => {
  const drivers = filter(getDriverListForOccupationList(appData), (item) => item.eligible).map((item) => item.driver);
  return drivers.find((driver) => driver.id === appData.currentOccupationListDriver?.id) || drivers[0];
};

export const getNextOccupationListDriver = (appData: AppData, currentID: string) => {
  const list = getDriverListForOccupationList(appData);
  const currentDriver = getCurrentOccupationListDriver(appData);
  const currentIndex = list.findIndex((item) => item.driver.id === (currentID || currentDriver?.id));

  if (currentIndex < 0) {
    return;
  }

  return list.find((item, index) => index > currentIndex && item.eligible)?.driver;
};

// Ask driver for occupation details
export const OccupationListDriverStep: Step = {
  id: 'occupationListDriver',
  pathname: '/occupationListDriver',

  canShow: (appData: AppData) => {
    const driver = getCurrentOccupationListDriver(appData);
    return !!driver?.id;
  },
  hasData: () => true,
  hasOwnData: () => true,
  queryParams: (appData: AppData, router: NextRouter) => {
    return {
      olDrvId: getCurrentOccupationListDriver(appData)?.id || router.query.olDrvId || null,
    };
  },
  category: () => StepCategory.Drivers,
  specialNextStep: {
    nextStepId: 'occupationListDriver',
    enable: (appData: AppData) => {
      return !!appData?.currentOccupationListDriver?.id;
    },
  },
};
